import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  addMonths,
  subDays,
  parseISO,
  isValid,
} from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

const locales = { "pt-BR": ptBR, "en-US": enUS };
type LocaleKey = keyof typeof locales;

// Padrões com palavras literais ('às', 'hoje') variam por locale — nunca
// fixar o padrão pt-BR para não vazar PT em telas EN (issue #91).
const patterns: Record<LocaleKey, { date: string; dateTime: string; today: string; yesterday: string }> = {
  "pt-BR": {
    date: "dd/MM/yyyy",
    dateTime: "dd/MM/yyyy 'às' HH:mm",
    today: "'hoje,' HH:mm",
    yesterday: "'ontem,' HH:mm",
  },
  "en-US": {
    date: "MM/dd/yyyy",
    dateTime: "MM/dd/yyyy 'at' h:mm a",
    today: "'today,' h:mm a",
    yesterday: "'yesterday,' h:mm a",
  },
};

function getLocale(): LocaleKey {
  const lang = document.documentElement.lang;
  return lang in locales ? (lang as LocaleKey) : "pt-BR";
}

export function formatDate(value: Date | string, pattern?: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const locale = getLocale();
  return format(date, pattern ?? patterns[locale].date, {
    locale: locales[locale],
  });
}

export function formatDateTime(value: Date | string): string {
  return formatDate(value, patterns[getLocale()].dateTime);
}

/**
 * Fim do contrato de locação a partir do início + prazo: **start + N meses − 1 dia**
 * (último dia da vigência — ex.: 12 meses de 2026-01-01 → 2026-12-31). Entrada/saída em
 * "yyyy-MM-dd" (o que o `<input type="date">` usa). Retorna `null` se a data for inválida
 * ou o prazo < 1 mês.
 */
export function contractEndFromTerm(startIso: string, months: number): string | null {
  if (!startIso || !Number.isFinite(months) || months < 1) return null;
  const start = parseISO(startIso);
  if (!isValid(start)) return null;
  return format(subDays(addMonths(start, months), 1), "yyyy-MM-dd");
}

/**
 * Formata uma **data-only** ("yyyy-MM-dd") no locale atual SEM deslocar o fuso. `new Date("yyyy-MM-dd")`
 * é interpretado como UTC e, em fusos negativos (BR, UTC−3), `toLocaleDateString`/`format` mostram o dia
 * ANTERIOR. `parseISO` trata data-only como meia-noite LOCAL. Use para datas sem hora (vencimentos,
 * início/fim de contrato). Retorna `null` para vazio/não-string. (QA 2026-06-25)
 */
export function formatDateOnly(value?: unknown): string | null {
  if (!value || typeof value !== "string") return null;
  const d = parseISO(value);
  return isValid(d) ? formatDate(d) : value;
}

export function formatRelative(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const locale = getLocale();
  if (isToday(date)) return formatDate(date, patterns[locale].today);
  if (isYesterday(date)) return formatDate(date, patterns[locale].yesterday);
  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: locales[locale],
  });
}
